import { SubmissionStepType } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { FrozenStepPanel } from "./FrozenStepPanel";

/**
 * Steps rendered from the frozen snapshot (read-only, snapshot-sourced) once the
 * submission is frozen. Details owns its read-only handling: it renders
 * submission metadata, which is not in the snapshot.
 */
export const SNAPSHOT_FROZEN_STEPS = new Set<SubmissionStepType>([
  SubmissionStepType.FacilityProfile,
  SubmissionStepType.RcraWasteActivity,
  SubmissionStepType.Contacts,
  SubmissionStepType.FilingNotifications,
  SubmissionStepType.StateFields,
  SubmissionStepType.Review,
]);

/**
 * Renders the real editor for a single stepper step, wiring the `submissionId`
 * and `facilityId` from the shell's context.
 *
 * When the submission is frozen, snapshot-backed sections render their read-only
 * snapshot panel instead of a live editor (no edit affordances or "mark complete
 * & continue" footers). Details handles its own read-only state internally, so
 * it always mounts its editor.
 */
export function StepRoute({ step }: { step: SubmissionStepType }) {
  const { submissionId, facilityId, isFrozen } = useWasteStepper();

  if (isFrozen && SNAPSHOT_FROZEN_STEPS.has(step)) {
    return <FrozenStepPanel step={step} submissionId={submissionId} />;
  }

  // OVERVIEW has no `Editor` (it renders `StepperOverview` on the index route),
  // so it never reaches `StepRoute`; the guard keeps the type honest.
  const { Editor } = WASTE_SUBMISSION_STEP_PRESENTATION[step];
  if (!Editor) {
    return null;
  }
  return <Editor submissionId={submissionId} facilityId={facilityId} />;
}
